/* How a skill reads anywhere it shows — the Skills page and an agent's Settings: the badge
   for who stands behind it, and what its security auditors found. */
import React from "react";
import { BadgeCheck, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";

import type { AuditStatus, SkillSource } from "@/shared/types";

/** Worst-of across the auditors: fail < warn < pass. `none` when nobody has audited it. */
export function auditSummary(audits: Record<string, AuditStatus>): AuditStatus | "none" {
  const verdicts = Object.values(audits);
  if (!verdicts.length) return "none";
  if (verdicts.includes("fail")) return "fail";
  if (verdicts.includes("warn")) return "warn";
  return "pass";
}

/* Filled check = managed by Definable, outline check = the vendor's own. */
export function SourceBadge({ source, size = 13 }: { source: SkillSource; size?: number }) {
  if (source === "definable") {
    return (
      <span title="Managed by Definable" className="shrink-0 inline-flex items-center">
        {/* fill as an attribute, not inline CSS — see the note in LeftSidebar. */}
        <BadgeCheck style={{ width: size, height: size }} fill="var(--text-primary)" className="text-[var(--surface-sunk)]" strokeWidth={1.75} />
      </span>
    );
  }
  if (source === "official") {
    return (
      <span title="Official — published by the vendor" className="shrink-0 inline-flex items-center">
        <BadgeCheck style={{ width: size, height: size }} className="text-[var(--text-tertiary)]" strokeWidth={1.75} />
      </span>
    );
  }
  return null;
}

const AUDIT_PILL: Record<AuditStatus | "none", { label: string; cls: string }> = {
  pass: { label: "Pass", cls: "bg-[var(--success-bg)] text-[var(--success)] ring-[var(--success-ring)]" },
  warn: { label: "Warn", cls: "bg-[var(--warn-bg)] text-[var(--warn-text)] ring-[var(--warn-ring)]" },
  fail: { label: "Fail", cls: "bg-[var(--danger-bg)] text-[var(--danger)] ring-[var(--danger-ring)]" },
  none: { label: "Not yet", cls: "bg-[var(--surface-2)] text-[var(--text-tertiary)] ring-[var(--hairline)]" },
};

export function AuditPill({ status }: { status: AuditStatus | "none" }) {
  const p = AUDIT_PILL[status];
  return (
    <span className={`inline-flex items-center whitespace-nowrap px-2 py-[2px] rounded-full text-[11px] font-medium ring-1 ${p.cls}`}>
      {p.label}
    </span>
  );
}

/** The worst verdict across a skill's auditors, as one shield. */
export function AuditShield({ audits, withLabel = false, note }: {
  audits: Record<string, AuditStatus>;
  withLabel?: boolean;
  /** Why it reads as it does, in place of the verdict as the tooltip. */
  note?: string;
}) {
  const s = auditSummary(audits);
  const Icon = s === "none" ? ShieldQuestion : s === "pass" ? ShieldCheck : ShieldAlert;
  const color =
    s === "pass" ? "text-[var(--success)]"
    : s === "warn" ? "text-[var(--warn-text)]"
    : s === "fail" ? "text-[var(--danger)]"
    : "text-[var(--text-quaternary)]";
  const label = s === "pass" ? "Audited" : s === "none" ? "Not audited" : AUDIT_PILL[s].label;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap ${color}`} title={note ?? `Security audits: ${label}`}>
      <Icon className="w-3 h-3" strokeWidth={1.75} />
      {withLabel && <span className="text-[11.5px]">{label}</span>}
    </span>
  );
}
